import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NavBar from '../../components/common/NavBar';
import TimeSeriesChart from '../../components/common/TimeSeriesChart';
import CsvDownloadButton from '../../components/common/CsvDownloadButton';
import AboutThisData from '../../components/data/AboutThisData';
import DataStateNotice from '../../components/data/DataStateNotice';
import ResultCapNotice, { RESULT_ROW_CAP } from '../../components/data/ResultCapNotice';
import SourceBadge from '../../components/data/SourceBadge';
import DraftDataBadge from '../../components/data/DraftDataBadge';
import { ODMResult } from '../../services/odmApi';
import { GRANULARITY_LABELS, PRODUCT_LABELS, pendingFor } from '../../config/products';
import { buildChartSeries } from '../../utils/chartSeries';
import { csvFilename } from '../../utils/csv';
import type { Cell } from '../../utils/csv';
import { formatDayLabel } from '../../utils/dateWindow';
import { pluralize } from '../../utils/plural';
import { areaCount } from '../../utils/selectionSummary';
import { directionLabel } from '../../utils/odmDirection';
import type { OdmDirection } from '../../utils/odmDirection';
import { selectionAreaIds } from '../../utils/odmSelection';
import type { SelectedArea } from '../../utils/odmSelection';
import { COUNTRIES, normalizeCountry } from '../../config/countries';
import { CONTEXT_HEADERS, contextValues } from '../../utils/exportContext';
import { odmBackState } from '../../utils/returnState';
import type { WindowUtc } from '../../utils/submittedWindow';

// The preview endpoint returns at most this many rows.

interface ODMPreviewState {
  jobId: string;
  jobResults: ODMResult[];
  jobStatus?: any;
  /** The areas picked on the setup page and how trips were counted for them. */
  selection?: SelectedArea[];
  geoType?: string | null;
  direction?: OdmDirection | null;
  /** Handed back to the map page on the way home, so it can redraw the selection. */
  geometries?: any[];
  countryCode?: string;
  /** The delivery day and resolution the job was run for. */
  preservedDay?: string;
  preservedGranularity?: 'hourly' | 'daily';
  /** The start and end instants the job was submitted with, handed back with the rest. */
  windowUtc?: WindowUtc | null;
  /** The picker's query string, handed back with the rest. */
  pickerSearch?: string | null;
}

type SortField = 'origin_geo_id' | 'destination_geo_id' | 'trips' | 'time_bucket' | 'General_Distance';

/** The row's time bucket: the hour (hourly) or the delivery date (daily); a row with none falls back to its batch date. */
const bucketOf = (r: ODMResult): string => r.time_bucket ?? r.batch_date;

const ODMPreview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('origin_geo_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  const state = location.state as ODMPreviewState | null;

  // One line per origin-destination pair.
  const series = useMemo(
    () =>
      buildChartSeries(state?.jobResults ?? [], {
        key: (r) => `${r.origin_geo_id}→${r.destination_geo_id}`,
        label: (r) => `${r.origin_geo_id} → ${r.destination_geo_id}`,
        time: bucketOf,
        value: (r) => r.trips,
      }),
    [state?.jobResults],
  );

  // Check if we have multiple time periods
  const hasMultipleTimePeriods = useMemo(() => {
    if (!state?.jobResults) return false;
    const uniqueTimePeriods = new Set(state.jobResults.map(bucketOf));
    return uniqueTimePeriods.size > 1;
  }, [state?.jobResults]);

  // Auto-collapse table if it has many rows
  useEffect(() => {
    if (state?.jobResults) {
      setIsTableCollapsed(state.jobResults.length > 100);
    }
  }, [state?.jobResults]);

  useEffect(() => {
    if (!state?.jobResults) {
      navigate('/odm-area-select');
    }
  }, [state, navigate]);

  if (!state?.jobResults) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Preview Data</h2>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/odm-area-select')}
          >
            Return to {PRODUCT_LABELS.odm} setup
          </button>
        </div>
      </div>
    );
  }

  const { jobId, jobResults, selection = [], geoType = null, direction, countryCode, preservedDay, preservedGranularity } = state;

  // Where Back leads: the map page, with the job, both areas and the window it was run for. The
  // map page writes the same state into its own history entry before opening this page, so the
  // browser's Back button restores exactly what the Back button here does.
  const backState = odmBackState({ ...state, windowUtc: state.windowUtc ?? null });
  const country = normalizeCountry(countryCode);

  // Sorting logic
  const sortValue = (r: ODMResult) => (sortField === 'time_bucket' ? bucketOf(r) : r[sortField]);
  const sortedResults = [...jobResults].sort((a, b) => {
    const aValue = sortValue(a);
    const bValue = sortValue(b);

    // A missing value (no distance for this row) sorts after every number, whatever the direction.
    if (aValue == null || bValue == null) {
      if (aValue == null && bValue == null) return 0;
      return aValue == null ? 1 : -1;
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const aStr = String(aValue);
    const bStr = String(bValue);
    return sortDirection === 'asc'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = sortedResults.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Trips add up over routes and over time, so the total means something. An average over the
  // (at most 100) preview rows would not, so there is none.
  const totalTrips = jobResults.reduce((sum, result) => sum + (result.trips || 0), 0);
  const maxTrips = Math.max(...jobResults.map(r => r.trips || 0), 0);
  // A row's trips as a share of the busiest row (0 when no row has any trips).
  const tripShare = (r: ODMResult) => (maxTrips > 0 ? ((r.trips || 0) / maxTrips) * 100 : 0);
  // At the preview limit the rows are only the first part of the answer, so the total is too.
  const capped = jobResults.length >= RESULT_ROW_CAP;
  const uniqueOrigins = new Set(jobResults.map(r => r.origin_geo_id)).size;
  const uniqueDestinations = new Set(jobResults.map(r => r.destination_geo_id)).size;

  // Distance and speed are optional columns: statistics cover only the rows that have a value,
  // and a card is shown only when at least one row has one.
  const distances = jobResults.map(r => r.General_Distance).filter((d): d is number => d != null);
  const speeds = jobResults.map(r => r.Average_Speed).filter((v): v is number => v != null);
  const hasDistance = distances.length > 0;
  const hasSpeed = speeds.length > 0;
  const avgDistance = hasDistance ? Math.round(distances.reduce((sum, d) => sum + d, 0) / distances.length) : 0;
  const maxDistance = hasDistance ? Math.max(...distances) : 0;
  const avgSpeed = hasSpeed ? Math.round((speeds.reduce((sum, v) => sum + v, 0) / speeds.length) * 10) / 10 : 0;

  // The file states what it holds: country, source, measure and the UTC window of the job.
  const context: string[] = country
    ? contextValues(country, 'odm', 'trips', state.windowUtc ?? null)
    : CONTEXT_HEADERS.map(() => '');
  const csvRows: Cell[][] = [
    [
      'Origin ID',
      'Destination ID',
      'Trips',
      'Time bucket (UTC)',
      'Date',
      ...(hasDistance ? ['Distance (m)'] : []),
      ...(hasSpeed ? ['Average speed (km/h)'] : []),
      ...CONTEXT_HEADERS,
    ],
    ...jobResults.map((r): Cell[] => [
      r.origin_geo_id,
      r.destination_geo_id,
      r.trips,
      bucketOf(r),
      r.batch_date,
      ...(hasDistance ? [r.General_Distance] : []),
      ...(hasSpeed ? [r.Average_Speed] : []),
      ...context,
    ]),
  ];
  const csvName = csvFilename(
    'odm',
    [country ? COUNTRIES[country].label : '', preservedDay, preservedGranularity].filter(Boolean).join(' '),
  );

  return (
    <div className="min-h-screen bg-base-200">
      <NavBar title={`${PRODUCT_LABELS.odm} Results Preview`} showBackButton backTo="/odm" backState={backState} />

      <div className="container mx-auto px-4 py-6">
        {/* Header Section */}
        <div className="bg-surface rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-ink">{PRODUCT_LABELS.odm} Results</h1>
                {country ? <SourceBadge country={country} product="odm" /> : null}
                <DraftDataBadge country={country} product="odm" geoType={geoType} />
              </div>
              <p className="text-ink-muted mt-1">Job ID: {jobId}</p>
              {(preservedDay || preservedGranularity) && (
                <p className="text-ink-muted mt-1">
                  {preservedDay ? formatDayLabel(preservedDay) : ''}{preservedDay && preservedGranularity ? ' · ' : ''}{preservedGranularity ? GRANULARITY_LABELS[preservedGranularity] : ''} · UTC
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm text-ink-subtle">Total Results</div>
              <div className="text-2xl font-bold text-note-ink">{jobResults.length}</div>
            </div>
          </div>

          {/* Selection Summary */}
          <div className="mb-4 p-3 bg-note-soft rounded-lg border border-note-line">
            <h3 className="font-semibold text-note-ink mb-1">🔵 {direction ? directionLabel(direction) : 'Selection'}</h3>
            <p className="text-sm text-note-ink">
              {selection.length > 0
                ? `${areaCount(selectionAreaIds(selection).length, geoType, country)}: ${selection.map((a) => a.name).join(', ')}`
                : 'Not specified'}
            </p>
          </div>

          {country ? (
            <div className="mb-4">
              <AboutThisData country={country} product="odm" measure="trips" />
            </div>
          ) : null}

          {/* Summary Stats */}
          {jobResults.length === 0 ? (
            <div className="mb-4">
              <DataStateNotice
                tone="info"
                title="No rows for this selection"
                body={["Routes with fewer than 5 trips are not published.", pendingFor(country, 'odm', geoType)?.reason].filter(Boolean).join(' ')}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div role="group" aria-label="Total trips" className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{totalTrips.toLocaleString()}</div>
                <div className="text-sm text-ink-muted">
                  {capped ? `Total trips (first ${RESULT_ROW_CAP} rows)` : 'Total trips'}
                </div>
              </div>
              <div role="group" aria-label="Peak route" className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{maxTrips.toLocaleString()}</div>
                <div className="text-sm text-ink-muted">Peak Route</div>
              </div>
              <div role="group" aria-label="Origin to destination" className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{uniqueOrigins} → {uniqueDestinations}</div>
                <div className="text-sm text-ink-muted">Origin → Dest</div>
              </div>
            </div>
          )}
          <div className="mb-4 empty:hidden">
            <ResultCapNotice rows={jobResults} />
          </div>

          {/* Travel Stats: only what the data holds */}
          {(hasDistance || hasSpeed) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {hasDistance && (
                <>
                  <div className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">{avgDistance.toLocaleString()} m</div>
                    <div className="text-sm text-ink-muted">Avg distance (per row)</div>
                  </div>
                  <div className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">{maxDistance.toLocaleString()} m</div>
                    <div className="text-sm text-ink-muted">Max distance</div>
                  </div>
                </>
              )}
              {hasSpeed && (
                <div className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold">{avgSpeed.toLocaleString()} km/h</div>
                  <div className="text-sm text-ink-muted">Avg speed (per row)</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Time Series Chart */}
        {hasMultipleTimePeriods && (
          <div className="bg-surface rounded-lg shadow p-6 mb-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-ink flex items-center gap-2">
                📈 Movement Trends Over Time
              </h2>
              <p className="text-sm text-ink-muted mt-1">
                People movement patterns across selected time periods
              </p>
            </div>
            <div className="h-64">
              <TimeSeriesChart
                series={series}
                title="Trips Over Time"
                yLabel="Trips"
                seriesNoun="origin–destination pairs"
              />
            </div>
          </div>
        )}

        {/* Table Controls: with no rows there is nothing to page through or hide */}
        {jobResults.length > 0 && (
        <div className="bg-surface rounded-lg shadow p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setIsTableCollapsed(!isTableCollapsed)}
              >
                {isTableCollapsed ? '📊 Show Table' : '📊 Hide Table'}
              </button>
              {!isTableCollapsed && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-ink-muted">Show:</span>
                  <select
                    className="select select-sm select-bordered"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-ink-muted">results per page</span>
                </div>
              )}
            </div>

            <div className="text-sm text-ink-muted">
              {isTableCollapsed ? (
                `${pluralize(jobResults.length, 'result')} (table hidden)`
              ) : (
                `Showing ${startIndex + 1}-${Math.min(startIndex + itemsPerPage, jobResults.length)} of ${pluralize(jobResults.length, 'result')}`
              )}
            </div>
          </div>
        </div>
        )}

        {/* Results Table */}
        {jobResults.length > 0 && !isTableCollapsed && (
        <div className="bg-surface rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full text-sm">
              <thead>
                <tr>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('origin_geo_id')}
                  >
                    Origin ID {getSortIcon('origin_geo_id')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('destination_geo_id')}
                  >
                    Destination ID {getSortIcon('destination_geo_id')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('trips')}
                  >
                    Trips {getSortIcon('trips')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('time_bucket')}
                  >
                    Time bucket (UTC) {getSortIcon('time_bucket')}
                  </th>
                  {hasDistance && (
                    <th
                      className="cursor-pointer hover:bg-paper-100"
                      onClick={() => handleSort('General_Distance')}
                    >
                      Distance (m) {getSortIcon('General_Distance')}
                    </th>
                  )}
                  {hasSpeed && <th>Avg speed (km/h)</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedResults.map((result, index) => (
                  <tr key={`${result.origin_geo_id}-${result.destination_geo_id}-${index}`}>
                    <td className="font-mono text-xs">{result.origin_geo_id}</td>
                    <td className="font-mono text-xs">{result.destination_geo_id}</td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-note-ink">
                          {(result.trips || 0).toLocaleString()}
                        </span>
                        <div
                          className="w-12 h-2 bg-paper-300 rounded-full overflow-hidden"
                          title={`${tripShare(result).toFixed(1)}% of max`}
                        >
                          <div
                            className="h-full bg-series-2 rounded-full"
                            style={{
                              width: `${tripShare(result)}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="text-xs">{bucketOf(result)}</td>
                    {hasDistance && (
                      <td className="font-mono text-xs">
                        {result.General_Distance == null ? '–' : result.General_Distance.toLocaleString()}
                      </td>
                    )}
                    {hasSpeed && (
                      <td className="font-mono text-xs">
                        {result.Average_Speed == null ? '–' : result.Average_Speed.toLocaleString()}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <button
                className="btn btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              <div className="flex items-center space-x-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + Math.max(1, currentPage - 2);
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="text-ink-subtle">...</span>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                className="btn btn-sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center mt-6 space-x-4">
          <button
            className="btn btn-outline"
            onClick={() => navigate('/odm', { state: backState })}
          >
            ← Back to {PRODUCT_LABELS.odm}
          </button>
          <CsvDownloadButton rows={csvRows} filename={csvName} disabled={jobResults.length === 0} />
        </div>
      </div>
    </div>
  );
};

export default ODMPreview;